import { useEffect, useState } from "react";
import { useGetList, useRecordContext, useUpdate } from "ra-core";

import { VOGELKOP_BUDGETS } from "../../../../supabase/functions/_shared/vogelkop.ts";
import type { Campaign, Contact, LeadSource } from "../types";
import { ServicesChecklist } from "./ServicesChecklist";

const savePatch = (
  update: ReturnType<typeof useUpdate>[0],
  record: Contact,
  data: Partial<Contact>,
) => {
  update("contacts", {
    id: record.id,
    data,
    previousData: record,
  });
};

const InlineText = ({
  label,
  value,
  onSave,
}: {
  label: string;
  value: string;
  onSave: (next: string) => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);

  return (
    <label className="grid grid-cols-[7.5rem_1fr] items-center gap-2 border-b py-2 text-sm">
      <span>{label}</span>
      {editing ? (
        <input
          autoFocus
          className="h-8 rounded-md border px-2"
          value={draft}
          aria-label={label}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            setEditing(false);
            if (draft !== value) onSave(draft);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.currentTarget.blur();
          }}
        />
      ) : (
        <button
          type="button"
          className="min-h-8 whitespace-normal break-words rounded-md border px-2 py-1 text-left text-sky-700"
          onClick={() => setEditing(true)}
        >
          {value || "Add"}
        </button>
      )}
    </label>
  );
};

const InlineSelect = ({
  label,
  value,
  options,
  onSave,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onSave: (next: string) => void;
}) => (
  <label className="grid grid-cols-[7.5rem_1fr] items-center gap-2 border-b py-2 text-sm">
    <span>{label}</span>
    <select
      aria-label={label}
      className="h-8 rounded-md border bg-transparent px-2"
      value={value}
      onChange={(event) => onSave(event.target.value)}
    >
      <option value="">—</option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </label>
);

export const LeadProfile = () => {
  const record = useRecordContext<Contact>();
  const [update] = useUpdate();
  const { data: sources = [] } = useGetList<LeadSource>("lead_sources", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: campaigns = [] } = useGetList<Campaign>("campaigns", {
    pagination: { page: 1, perPage: 50 },
    sort: { field: "name", order: "ASC" },
  });
  if (!record) return null;

  const phone = record.phone_jsonb?.find((item) => item.number)?.number ?? "";
  const email = record.email_jsonb?.find((item) => item.email)?.email ?? "";

  return (
    <aside className="sticky top-4 max-h-[calc(100vh-2rem)] w-[22rem] shrink-0 overflow-y-auto rounded-xl border bg-card p-4">
      <h2 className="mb-3 text-lg font-semibold">
        {record.first_name} {record.last_name}
      </h2>
      <InlineText
        label="First name"
        value={record.first_name ?? ""}
        onSave={(next) => savePatch(update, record, { first_name: next })}
      />
      <InlineText
        label="Last name"
        value={record.last_name ?? ""}
        onSave={(next) => savePatch(update, record, { last_name: next })}
      />
      <InlineText
        label="Phone"
        value={phone}
        onSave={(next) =>
          savePatch(update, record, {
            phone_jsonb: [{ number: next, type: "Work" }],
          })
        }
      />
      <InlineText
        label="Email"
        value={email}
        onSave={(next) =>
          savePatch(update, record, {
            email_jsonb: [{ email: next, type: "Work" }],
          })
        }
      />
      <InlineText
        label="Address"
        value={record.property_address ?? ""}
        onSave={(next) => savePatch(update, record, { property_address: next })}
      />
      <InlineSelect
        label="Budget"
        value={record.budget ?? ""}
        options={VOGELKOP_BUDGETS.map((budget) => ({
          value: budget,
          label: budget,
        }))}
        onSave={(next) => savePatch(update, record, { budget: next || null })}
      />
      <InlineSelect
        label="Source"
        value={record.source_id == null ? "" : String(record.source_id)}
        options={sources.map((source) => ({
          value: String(source.id),
          label: source.name,
        }))}
        onSave={(next) =>
          savePatch(update, record, {
            source_id: next ? Number(next) : null,
          })
        }
      />
      <InlineSelect
        label="Campaign"
        value={record.campaign_id == null ? "" : String(record.campaign_id)}
        options={campaigns.map((campaign) => ({
          value: String(campaign.id),
          label: campaign.name,
        }))}
        onSave={(next) =>
          savePatch(update, record, {
            campaign_id: next ? Number(next) : null,
          })
        }
      />
      <InlineText
        label="Notes"
        value={record.background ?? ""}
        onSave={(next) => savePatch(update, record, { background: next })}
      />
      <div className="mt-4">
        <h3 className="mb-2 text-sm font-semibold">Services</h3>
        <ServicesChecklist />
      </div>
    </aside>
  );
};
